import React, { useState, useRef } from 'react';
import emailjs from '@emailjs/browser';
import ReCAPTCHA from 'react-google-recaptcha';
import '../styles/Contact.scss';

const Contact = () => {
  const form = useRef();
  const recaptchaRef = useRef();
  const contactContentRef = useRef();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [status, setStatus] = useState({
    loading: false,
    success: false,
    error: false,
    recaptchaError: false
  });
  const [recaptchaToken, setRecaptchaToken] = useState(null);

  const scrollToContactContent = () => {
    contactContentRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleRecaptchaChange = (token) => {
    setRecaptchaToken(token);
    // Clear reCAPTCHA error when user completes it
    if (token) {
      setStatus(prev => ({ ...prev, recaptchaError: false }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!recaptchaToken) {
      setStatus({ loading: false, success: false, error: false, recaptchaError: true });
      scrollToContactContent();
      return;
    }

    setStatus({ loading: true, success: false, error: false, recaptchaError: false });

    emailjs.sendForm(
      process.env.REACT_APP_EMAILJS_SERVICE_ID,
      process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
      form.current,
      process.env.REACT_APP_EMAILJS_PUBLIC_KEY
    )
      .then((result) => {
        setStatus({ loading: false, success: true, error: false, recaptchaError: false });
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: ''
        });
        setRecaptchaToken(null);
        recaptchaRef.current.reset();
        scrollToContactContent();
      })
      .catch((error) => {
        setStatus({ loading: false, success: false, error: true, recaptchaError: false });
        console.error('Error sending email:', error);
        setRecaptchaToken(null);
        recaptchaRef.current.reset();
        scrollToContactContent();
      });
  };

  return (
    <div className="contact">
      <section className="hero">
        <h1>Get in Touch</h1>
        <p>Do you have any questions or feedback? We'd love to hear from you!</p>
      </section>

      <section className="contact-content" ref={contactContentRef}>
        <div className="container">
          <div className="contact-grid">
            <div className="contact-info">
              <h2>Contact Information</h2>
              <div className="info-item">
                <h3>Email</h3>
                <p>info@omorsoft.com</p>
              </div>
              <div className="info-item">
                <h3>Location</h3>
                <p>86-90 Paul Street, London EC2A 4NE, UK</p>
              </div>
            </div>

            <div className="contact-form">
              <h2>Send us a Message</h2>
              {status.success && (
                <div className="success-message">
                  Thank you for your message! We'll get back to you soon.
                </div>
              )}
              {status.error && (
                <div className="error-message">
                  Sorry, there was an error sending your message. Please try again.
                </div>
              )}
              {status.recaptchaError && (
                <div className="error-message">
                  Please complete the reCAPTCHA verification before submitting.
                </div>
              )}
              <form ref={form} onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="name">Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="subject">Subject</label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="message">Message</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group recaptcha-group">
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                    onChange={handleRecaptchaChange}
                  />
                </div>
                <button 
                  type="submit" 
                  className="cta-button"
                  disabled={status.loading}
                >
                  {status.loading ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact; 